/**
 * @file     FactList.tsx
 * @purpose  Hub fact list: a hairline definition list in one or two columns,
 *           label in secondary ink on the left, value at 500 with tabular
 *           figures on the right. A null, empty or placeholder value drops its
 *           row, so a dash or "Unavailable" never reads as a fact. A row with
 *           `onActivate` is one button the size of the row (gated teasers).
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { layoutTokens } from '@/design-system/tokens/layout';
import { hairline, hubColors, isDisplayableFigure, reducedMotionSx, transition } from './hubKitStyles';

export interface FactItem {
  label: string;
  value: ReactNode;
  /** A decorative icon before the label (aria-hidden; the label stays visible). */
  icon?: ReactNode;
  /** Makes the whole row one button, e.g. a gated teaser that opens its gate. */
  onActivate?: () => void;
  /** The button's accessible name; required with `onActivate`. */
  activateLabel?: string;
}

export interface FactListProps {
  items: FactItem[];
  /** Two columns from md (default), or one. */
  columns?: 1 | 2;
  ariaLabel?: string;
}

function hasValue(value: ReactNode): boolean {
  if (value == null || typeof value === 'boolean') return false;
  if (typeof value === 'string' || typeof value === 'number') return isDisplayableFigure(value);
  return true;
}

export function FactList({ items, columns = 2, ariaLabel }: FactListProps) {
  const rows = items.filter((item) => hasValue(item.value));
  if (rows.length === 0) return null;
  return (
    <Box
      component="dl"
      aria-label={ariaLabel}
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: columns === 2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
        columnGap: { md: 8 },
      }}
    >
      {rows.map((item) => (
        <Box
          key={item.label}
          data-fact-row-action={item.onActivate ? '' : undefined}
          sx={{
            position: item.onActivate ? 'relative' : undefined,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 4,
            minHeight: 44,
            py: 2.5,
            borderBottom: hairline,
            minWidth: 0,
            ...(item.onActivate ? { '& > dt, & > dd': { position: 'relative', zIndex: 1, pointerEvents: 'none' } } : null),
          }}
        >
          {item.onActivate ? (
            // Painted under the label and value so the hover tint never dims the text.
            <Box
              component="button"
              type="button"
              aria-label={item.activateLabel}
              onClick={item.onActivate}
              sx={{
                position: 'absolute',
                // Down over the hairline so the target is the full 44px row.
                inset: '0 -8px -1px',
                zIndex: 0,
                m: 0,
                p: 0,
                border: 0,
                borderRadius: `${layoutTokens.radius.sm}px`,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: transition('background-color'),
                ...reducedMotionSx,
                '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.interactiveHover } },
                '&:active': { backgroundColor: hubColors.surfaceSunken },
                '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 0 },
              }}
            />
          ) : null}
          <Box component="dt" sx={{ m: 0, fontSize: 15, lineHeight: 1.45, color: hubColors.textSecondary, flexShrink: 0, maxWidth: '45%' }}>
            {item.icon ? (
              <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'text-bottom', mr: 2.5 }}>
                {item.icon}
              </Box>
            ) : null}
            {item.label}
          </Box>
          <Box
            component="dd"
            sx={{
              m: 0,
              fontSize: 15,
              lineHeight: 1.45,
              fontWeight: fontWeightTokens.medium,
              color: hubColors.textPrimary,
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'end',
              overflowWrap: 'anywhere',
              minWidth: 0,
            }}
          >
            {item.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
